// @ts-nocheck
/**
 * FileUploader Component
 * Carbon Design System file upload interface with multi-media support
 * Epic 75 - Work Item 3: R2 Object Storage and Content Delivery Architecture
 */

import React, { useState, useCallback } from 'react';
import {
  FileUploader as CarbonFileUploader,
  FileUploaderDropContainer,
  FileUploaderItem,
  InlineNotification,
  ProgressBar,
  Tag,
  Select,
  SelectItem,
  TextInput,
  TextArea,
  Toggle,
  Button,
  Form,
  FormGroup,
  Stack,
  Tile,
} from '@carbon/react';
import {
  CloudUpload,
  Document,
  Video,
  Image,
  Music,
  Portfolio,
  Certificate,
  Upload,
  CheckmarkFilled,
  ErrorFilled,
  InProgress,
} from '@carbon/react/icons';

interface FileUploadProps {
  onUploadComplete?: (fileData: UploadedFile) => void;
  onError?: (error: string) => void;
  category?: FileCategory;
  maxFileSize?: number;
  allowedTypes?: string[];
  familyId?: string;
  classId?: string;
  courseId?: string;
  assignmentId?: string;
  portfolioItemId?: string;
  defaultVisibility?: 'private' | 'public' | 'unlisted';
}

interface UploadedFile {
  fileId: string;
  fileName: string;
  fileSize: number;
  cdnUrl: string;
  thumbnailUrl?: string;
  category: string;
  fileType: string;
  visibility: string;
}

type FileCategory = 'document' | 'video' | 'image' | 'audio' | 'assignment' | 'portfolio' | 'resource';
type FileType =
  | 'project'
  | 'assignment'
  | 'certification'
  | 'achievement'
  | 'article'
  | 'presentation'
  | 'course_material'
  | 'submission'
  | 'resource';

const FILE_SIZE_LIMITS: Record<FileCategory, number> = {
  document: 50 * 1024 * 1024, // 50MB
  video: 5 * 1024 * 1024 * 1024, // 5GB
  image: 10 * 1024 * 1024, // 10MB
  audio: 100 * 1024 * 1024, // 100MB
  assignment: 50 * 1024 * 1024, // 50MB
  portfolio: 100 * 1024 * 1024, // 100MB
  resource: 100 * 1024 * 1024, // 100MB
};

const ALLOWED_MIME_TYPES: Record<FileCategory, string[]> = {
  document: ['.pdf', '.doc', '.docx', '.txt', '.ppt', '.pptx'],
  video: ['.mp4', '.webm', '.mov', '.avi', '.mpeg'],
  image: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
  audio: ['.mp3', '.wav', '.ogg', '.webm', '.m4a'],
  assignment: ['.pdf', '.doc', '.docx', '.zip', '.txt'],
  portfolio: ['.pdf', '.jpg', '.jpeg', '.png', '.mp4', '.zip'],
  resource: ['.zip', '.pdf', '.doc', '.docx'],
};

const getCategoryIcon = (category: FileCategory) => {
  const iconProps = { size: 20 };
  switch (category) {
    case 'document':
      return <Document {...iconProps} />;
    case 'video':
      return <Video {...iconProps} />;
    case 'image':
      return <Image {...iconProps} />;
    case 'audio':
      return <Music {...iconProps} />;
    case 'portfolio':
      return <Portfolio {...iconProps} />;
    case 'assignment':
      return <Certificate {...iconProps} />;
    default:
      return <CloudUpload {...iconProps} />;
  }
};

export const FileUploader: React.FC<FileUploadProps> = ({
  onUploadComplete,
  onError,
  category: defaultCategory = 'document',
  maxFileSize,
  allowedTypes,
  familyId,
  classId,
  courseId,
  assignmentId,
  portfolioItemId,
  defaultVisibility = 'private',
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadStatus, setUploadStatus] = useState<Record<string, 'uploading' | 'complete' | 'error'>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form fields
  const [category, setCategory] = useState<FileCategory>(defaultCategory);
  const [fileType, setFileType] = useState<FileType>('resource');
  const [visibility, setVisibility] = useState(defaultVisibility);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file size
      const sizeLimit = maxFileSize || FILE_SIZE_LIMITS[category];
      if (file.size > sizeLimit) {
        return `File size exceeds ${(sizeLimit / (1024 * 1024)).toFixed(0)}MB limit`;
      }

      // Check file type
      const allowedFileTypes = allowedTypes || ALLOWED_MIME_TYPES[category];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedFileTypes.some(ext => fileExtension === ext.toLowerCase())) {
        return `File type ${fileExtension} not allowed for ${category}`;
      }

      return null;
    },
    [category, maxFileSize, allowedTypes]
  );

  const handleFileChange = useCallback(
    (event: any) => {
      const newFiles = Array.from(event.target.files || []) as File[];
      const validFiles: File[] = [];
      const newErrors: Record<string, string> = {};

      newFiles.forEach(file => {
        const error = validateFile(file);
        if (error) {
          newErrors[file.name] = error;
        } else {
          validFiles.push(file);
        }
      });

      setFiles(prevFiles => [...prevFiles, ...validFiles]);
      setErrors(prevErrors => ({ ...prevErrors, ...newErrors }));
    },
    [validateFile]
  );

  const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    formData.append('fileType', fileType);
    formData.append('visibility', visibility);
    if (description) formData.append('description', description);
    if (tags) formData.append('tags', tags);
    if (familyId) formData.append('familyId', familyId);
    if (classId) formData.append('classId', classId);
    if (courseId) formData.append('courseId', courseId);
    if (assignmentId) formData.append('assignmentId', assignmentId);
    if (portfolioItemId) formData.append('portfolioItemId', portfolioItemId);

    setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
    setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

    try {
      // Simulate progress for demo (in production, use XMLHttpRequest for progress)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: Math.min((prev[file.name] || 0) + 20, 90),
        }));
      }, 500);

      const response = await fetch('/api/v1/storage/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      const data = await response.json();

      setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
      setUploadStatus(prev => ({ ...prev, [file.name]: 'complete' }));

      if (onUploadComplete) {
        onUploadComplete({
          ...data,
          category,
          fileType,
          visibility,
        });
      }

      return data;
    } catch (error: any) {
      setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
      setErrors(prev => ({ ...prev, [file.name]: error.message }));

      if (onError) {
        onError(error.message);
      }

      throw error;
    }
  };

  const handleUpload = async () => {
    const uploadPromises = files.map(file => {
      if (!uploadStatus[file.name]) {
        return uploadFile(file);
      }
      return Promise.resolve();
    });

    await Promise.all(uploadPromises);
  };

  const removeFile = (fileName: string) => {
    setFiles(prev => prev.filter(f => f.name !== fileName));
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[fileName];
      return newStatus;
    });
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fileName];
      return newErrors;
    });
  };

  const getStatusIcon = (fileName: string) => {
    const status = uploadStatus[fileName];
    if (status === 'complete') return <CheckmarkFilled style={{ color: 'var(--cds-support-success)' }} />;
    if (status === 'error') return <ErrorFilled style={{ color: 'var(--cds-support-error)' }} />;
    if (status === 'uploading') return <InProgress style={{ color: 'var(--cds-interactive-01)' }} />;
    return null;
  };

  return (
    <div className="file-uploader-container">
      <Stack gap={6}>
        {/* Upload Configuration */}
        <Tile>
          <Form>
            <Stack gap={5}>
              <FormGroup legendText="Upload Settings">
                <Stack gap={4}>
                  <Select
                    id="category"
                    labelText="File Category"
                    value={category}
                    onChange={e => setCategory(e.target.value as FileCategory)}
                    helperText="Select the type of content you're uploading"
                  >
                    <SelectItem value="document" text="Document" />
                    <SelectItem value="video" text="Video" />
                    <SelectItem value="image" text="Image" />
                    <SelectItem value="audio" text="Audio" />
                    <SelectItem value="assignment" text="Assignment" />
                    <SelectItem value="portfolio" text="Portfolio" />
                    <SelectItem value="resource" text="Resource" />
                  </Select>

                  <Select
                    id="fileType"
                    labelText="Content Type"
                    value={fileType}
                    onChange={e => setFileType(e.target.value as FileType)}
                  >
                    <SelectItem value="project" text="Project" />
                    <SelectItem value="assignment" text="Assignment" />
                    <SelectItem value="certification" text="Certification" />
                    <SelectItem value="achievement" text="Achievement" />
                    <SelectItem value="article" text="Article" />
                    <SelectItem value="presentation" text="Presentation" />
                    <SelectItem value="course_material" text="Course Material" />
                    <SelectItem value="submission" text="Submission" />
                    <SelectItem value="resource" text="Resource" />
                  </Select>

                  <Select
                    id="visibility"
                    labelText="Visibility"
                    value={visibility}
                    onChange={e => setVisibility(e.target.value as any)}
                    helperText="Control who can access this file"
                  >
                    <SelectItem value="private" text="Private - Only you" />
                    <SelectItem value="unlisted" text="Unlisted - Anyone with link" />
                    <SelectItem value="public" text="Public - Everyone" />
                  </Select>

                  <TextArea
                    id="description"
                    labelText="Description (optional)"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Add a description for your file..."
                    rows={3}
                  />

                  <TextInput
                    id="tags"
                    labelText="Tags (optional)"
                    value={tags}
                    onChange={e => setTags(e.target.value)}
                    placeholder="Enter tags separated by commas..."
                    helperText="e.g., homework, math, project"
                  />
                </Stack>
              </FormGroup>
            </Stack>
          </Form>
        </Tile>

        {/* File Upload Area */}
        <div className="upload-area">
          <FileUploaderDropContainer
            accept={ALLOWED_MIME_TYPES[category]}
            labelText={`Drag and drop ${category} files here or click to upload`}
            multiple
            onAddFiles={handleFileChange}
          />

          {/* File List */}
          {files.length > 0 && (
            <div className="file-list" style={{ marginTop: 'var(--cds-spacing-05)' }}>
              <h4 style={{ marginBottom: 'var(--cds-spacing-03)' }}>Files to Upload</h4>
              <Stack gap={3}>
                {files.map(file => (
                  <FileUploaderItem
                    key={file.name}
                    name={file.name}
                    status={uploadStatus[file.name] || 'edit'}
                    size="default"
                    onDelete={() => removeFile(file.name)}
                    errorSubject={errors[file.name]}
                    errorBody={errors[file.name]}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-02)' }}>
                        {getCategoryIcon(category)}
                        <span>{file.name}</span>
                        <Tag size="sm" kind="gray">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Tag>
                      </div>
                      {getStatusIcon(file.name)}
                    </div>
                    {uploadStatus[file.name] === 'uploading' && (
                      <ProgressBar
                        value={uploadProgress[file.name] || 0}
                        max={100}
                        size="small"
                        label={`${uploadProgress[file.name] || 0}%`}
                        helperText="Uploading..."
                      />
                    )}
                  </FileUploaderItem>
                ))}
              </Stack>

              <div
                style={{
                  marginTop: 'var(--cds-spacing-05)',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 'var(--cds-spacing-03)',
                }}
              >
                <Button
                  kind="secondary"
                  onClick={() => setFiles([])}
                  disabled={Object.values(uploadStatus).some(s => s === 'uploading')}
                >
                  Clear All
                </Button>
                <Button
                  kind="primary"
                  onClick={handleUpload}
                  disabled={files.length === 0 || Object.values(uploadStatus).some(s => s === 'uploading')}
                  renderIcon={Upload}
                >
                  Upload Files
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Success/Error Notifications */}
        {Object.entries(uploadStatus).filter(([_, status]) => status === 'complete').length > 0 && (
          <InlineNotification
            kind="success"
            title="Upload Complete"
            subtitle={`${Object.entries(uploadStatus).filter(([_, status]) => status === 'complete').length} file(s) uploaded successfully`}
          />
        )}

        {Object.keys(errors).length > 0 && (
          <InlineNotification
            kind="error"
            title="Upload Errors"
            subtitle="Some files could not be uploaded. Please check the errors above."
          />
        )}
      </Stack>
    </div>
  );
};
